/**
 * Work Log Review — frontend response shape.
 *
 * Mirrors the wire contract returned by the 159a backend route
 * (`GET /api/v1/orgs/:orgId/work-log-review`). When the backend shape
 * changes, this file and the matching Zod schema in
 * `src/data/schemas/workLogReview.ts` change with it.
 */

export type GroupByDimension =
  | 'app'
  | 'appFileName'
  | 'title'
  | 'rootDomain'
  | 'site'
  | 'url'
  | 'employee'
  | 'businessUnit'
  | 'project'
  | 'client'
  | 'classificationSource'
  | 'classifiedByRule'
  | 'day'
  | 'hour'
  | 'weekday';

export const GROUP_BY_DIMENSIONS: readonly GroupByDimension[] = [
  'app',
  'appFileName',
  'title',
  'rootDomain',
  'site',
  'url',
  'employee',
  'businessUnit',
  'project',
  'client',
  'classificationSource',
  'classifiedByRule',
  'day',
  'hour',
  'weekday',
] as const;

export type ClassificationSource = 'rule' | 'fallback' | 'manual' | 'ai';

export type SortByColumn =
  | 'count'
  | 'hours'
  | 'distinctEmployees'
  | 'firstSeenAt'
  | 'lastSeenAt'
  | 'percentOfFiltered';

export interface WorkLogReviewGroupRow {
  /** Dimension → display value. `null` is preserved (renders as "(none)"). */
  key: Record<string, string | null>;
  count: number;
  hours: number;
  distinctEmployees: number;
  firstSeenAt: string;
  lastSeenAt: string;
  percentOfFiltered: number;
}

export interface WorkLogReviewEventRow {
  id: string;
  occurredAt: string;
  employeeId: string | null;
  employeeName: string | null;
  app: string | null;
  appFileName: string | null;
  title: string | null;
  url: string | null;
  site: string | null;
  rootDomain: string | null;
  project: { id: string; name: string; clientId: string; clientName: string } | null;
  classificationSource: ClassificationSource | null;
  classifiedByRuleId: string | null;
  billable: boolean | null;
}

export interface WorkLogReviewResponse {
  filter: {
    from: string;
    to: string;
    classificationSource: ClassificationSource[];
    employeeIds?: string[];
    businessUnitId?: string;
    classifiedByRuleId?: string;
    projectId?: string;
    clientId?: string;
    groupBy: GroupByDimension[];
    sortBy: SortByColumn;
    sortDir: 'asc' | 'desc';
  };
  totals: {
    eventCount: number;
    hours: number;
    distinctEmployees: number;
    distinctGroups: number;
  };
  groups: WorkLogReviewGroupRow[];
  groupCapHit: boolean;
  groupCap: number;
  events: {
    page: number;
    pageSize: number;
    totalCount: number;
    rows: WorkLogReviewEventRow[];
  };
}

/**
 * Inputs for the data hook. Filters that flow into the URL query string.
 * `dateRange` is intentionally absent — the page reads it from `AppContext`
 * (DESIGN-RULES Rule 35).
 */
export interface WorkLogReviewQuery {
  /** ISO date (YYYY-MM-DD). Both ends required when supplied. */
  from?: string;
  to?: string;
  classificationSource?: ClassificationSource[];
  employeeIds?: string[];
  businessUnitId?: string;
  classifiedByRuleId?: string;
  projectId?: string;
  clientId?: string;
  groupBy?: GroupByDimension[];
  sortBy?: SortByColumn;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}
